import { useState, useEffect, useCallback } from 'react'
import { AssortmentAccessFilters } from '@/components/AssortmentAccess/AssortmentAccessFilters'
import { AssortmentAccessGrid } from '@/components/AssortmentAccess/AssortmentAccessGrid'
import { ManageSharesDialog } from '@/components/AssortmentAccess/ManageSharesDialog'
import { TransferOwnershipDialog } from '@/components/AssortmentAccess/TransferOwnershipDialog'
import { DeletePlanDialog } from '@/components/AssortmentAccess/DeletePlanDialog'
import { Pagination } from '@/components/common/Pagination'
import { useAdminPlansList } from '@/hooks/useAdminPlans'
import { toast } from '@/stores/useToastStore'
import type { ApiPlanSummary, ApiPlanStatus } from '@/types'

export default function AdminAssortmentAccess() {
  // Filter state
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState<ApiPlanStatus | 'all'>('all')
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  // Dialog state
  const [sharesDialogOpen, setSharesDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<ApiPlanSummary | null>(null)

  const [transferDialogOpen, setTransferDialogOpen] = useState(false)
  const [planToTransfer, setPlanToTransfer] = useState<ApiPlanSummary | null>(
    null
  )

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [planToDelete, setPlanToDelete] = useState<ApiPlanSummary | null>(null)

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [searchInput, statusFilter, ownerFilter, pageSize])

  // Query
  const { data, isLoading, isError } = useAdminPlansList({
    search: searchInput,
    status: statusFilter,
    planType: 'all',
    ownerId: ownerFilter ?? '',
    page,
    pageSize,
  })

  useEffect(() => {
    if (isError) {
      toast.error('Failed to load plans')
    }
  }, [isError])

  const plans = data?.data ?? []
  const total = data?.pagination.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  // Handlers
  const handleManageShares = useCallback((plan: ApiPlanSummary) => {
    setSelectedPlan(plan)
    setSharesDialogOpen(true)
  }, [])

  const handleTransferOwnership = useCallback((plan: ApiPlanSummary) => {
    setPlanToTransfer(plan)
    setTransferDialogOpen(true)
  }, [])

  const handleDeletePlan = useCallback((plan: ApiPlanSummary) => {
    setPlanToDelete(plan)
    setDeleteDialogOpen(true)
  }, [])

  return (
    <>
      {/* Dialogs */}
      <ManageSharesDialog
        open={sharesDialogOpen}
        onOpenChange={setSharesDialogOpen}
        plan={selectedPlan}
      />
      <TransferOwnershipDialog
        open={transferDialogOpen}
        onOpenChange={setTransferDialogOpen}
        plan={planToTransfer}
      />
      <DeletePlanDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        plan={planToDelete}
      />

      <div className="flex flex-col gap-6 p-6">
        {/* Page Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            Manage Assortment Access
          </h1>
          <p className="text-base text-muted-foreground mt-1">
            View, share, transfer ownership, and manage assortment plans
          </p>
        </div>

        {/* Filter Bar */}
        <AssortmentAccessFilters
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          ownerFilter={ownerFilter}
          onOwnerFilterChange={setOwnerFilter}
        />

        {/* Grid */}
        <AssortmentAccessGrid
          plans={plans}
          onManageShares={handleManageShares}
          onTransferOwnership={handleTransferOwnership}
          onDeletePlan={handleDeletePlan}
          loading={isLoading}
        />

        {/* Pagination */}
        <Pagination
          page={page}
          pageSize={pageSize}
          totalPages={totalPages}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>
    </>
  )
}
